import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { 
  PlusIcon, 
  SearchIcon, 
  BellIcon, 
  SettingsIcon, 
  ChevronDownIcon,
  ZapIcon,
  CommandIcon,
  FolderIcon,
  UsersIcon,
  RefreshCwIcon,
  WifiIcon,
  WifiOffIcon,
  MoreHorizontalIcon,
  KeyboardIcon,
  LayoutGrid,
  Columns,
  MessageCircle,
  Calendar,
  StickyNote,
  Mail,
  Users,
  Settings2,
  Minimize2,
  Maximize2,
  Lock,
  Unlock,
  Trash2,
  Loader2,
  Bot,
  Save,
  FolderOpen,
  Star,
  HelpCircle,
  FileText,
  MessageSquare,
  Info
} from 'lucide-react'
import { Button } from '../ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuShortcut
} from '../ui/dropdown-menu'
import { Input } from '../ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog'
import { FloatingInput } from '../ui/floating-input'
import CommandPalette from './CommandPalette'
import KeyboardShortcuts from './KeyboardShortcuts'
import LayoutManager from './LayoutManager'
import NotificationBell from '../notifications/NotificationBell'
import { trpc } from '../../lib/trpc'
import { useAuthStore } from '../../stores/auth-store'
import { cn } from '../../lib/utils'
import { shouldProcessHotkey } from '../../lib/keyboard-utils'
import { useLayoutPersistence } from '../../hooks/use-layout-persistence'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog'

interface WorkspaceHeaderConsolidatedProps {
  workspaceId: string
  panels?: any[]
  currentPanel?: {
    type: string
    id: string
    title?: string
  }
  onNavigate?: (path: string) => void
  onCreatePanel?: (type: string) => void
  onAction?: (action: string) => void
  className?: string
}

const PANEL_TYPES = [
  { 
    type: 'KANBAN', 
    icon: Columns, 
    label: 'Kanban',
    description: 'Task management with drag-and-drop columns',
    defaultTitle: 'New Kanban'
  },
  { 
    type: 'CHAT', 
    icon: MessageCircle, 
    label: 'Chat',
    description: 'Team communication and messaging',
    defaultTitle: 'Team Chat'
  },
  { 
    type: 'AI_CHAT', 
    icon: Bot, 
    label: 'AI Assistant',
    description: 'Gemini 2.5 Flash AI-powered assistant',
    defaultTitle: 'AI Assistant'
  },
  { 
    type: 'CALENDAR', 
    icon: Calendar, 
    label: 'Calendar',
    description: 'Schedule and event management',
    defaultTitle: 'Calendar'
  },
  { 
    type: 'NOTES', 
    icon: StickyNote, 
    label: 'Notes',
    description: 'Quick notes and documentation',
    defaultTitle: 'Notes'
  },
  { 
    type: 'EMAIL', 
    icon: Mail, 
    label: 'Email',
    description: 'Email management and communication',
    defaultTitle: 'Email'
  },
  { 
    type: 'CONTACTS', 
    icon: Users, 
    label: 'Contacts',
    description: 'Contact and customer management',
    defaultTitle: 'Contacts'
  },
  { 
    type: 'DASHBOARD', 
    icon: LayoutGrid, 
    label: 'Dashboard',
    description: 'Analytics and performance overview',
    defaultTitle: 'Dashboard'
  }
] as const

export default function WorkspaceHeaderConsolidated({
  workspaceId,
  panels = [],
  currentPanel,
  onNavigate,
  onCreatePanel,
  onAction,
  className
}: WorkspaceHeaderConsolidatedProps) {
  const navigate = useNavigate()
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false)
  const [isKeyboardShortcutsOpen, setIsKeyboardShortcutsOpen] = useState(false)
  const [isCustomPanelOpen, setIsCustomPanelOpen] = useState(false)
  const [isMobileWorkspaceOpen, setIsMobileWorkspaceOpen] = useState(false)
  const [isMobileCreateOpen, setIsMobileCreateOpen] = useState(false)
  const [isMobileNotificationsOpen, setIsMobileNotificationsOpen] = useState(false)
  const [isMobileProfileOpen, setIsMobileProfileOpen] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [customTitle, setCustomTitle] = useState('')
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveForm, setSaveForm] = useState({
    name: '',
    description: '',
    isDefault: false
  })
  const { user } = useAuthStore()

  // Layout persistence
  const {
    presets,
    savePreset,
    loadPreset,
    deletePreset,
    setDefaultPreset,
    performAutoSave,
    isSaving,
    isLoading,
    isDeleting,
    isSettingDefault,
    isAutoSaving,
    refetchPresets
  } = useLayoutPersistence({
    workspaceId,
    panels,
    enabled: true
  })

  // tRPC hooks
  const utils = trpc.useUtils()
  const { data: workspaces = [] } = trpc.workspace.list.useQuery()
  const { data: currentWorkspace } = trpc.workspace.findById.useQuery(
    { id: workspaceId },
    { enabled: !!workspaceId }
  )
  const { data: notificationData } = trpc.notification.list.useQuery({})
  const notifications = Array.isArray(notificationData) ? notificationData : (notificationData?.items || [])
  const { data: kanbans = [] } = trpc.kanban.list.useQuery({ workspaceId })
  const { data: chatThreads = [] } = trpc.chat.listThreads.useQuery({ workspaceId })

  // Panel mutations
  const createPanel = trpc.panel.create.useMutation({
    onSuccess: () => {
      utils.panel.list.invalidate({ workspaceId })
      setIsCustomPanelOpen(false)
      setSelectedType(null)
      setCustomTitle('')
    }
  })

  const toggleMinimizeAll = trpc.panel.toggleMinimizeAll.useMutation({
    onSuccess: () => utils.panel.list.invalidate({ workspaceId })
  })

  const toggleLockAll = trpc.panel.toggleLockAll.useMutation({
    onSuccess: () => utils.panel.list.invalidate({ workspaceId })
  })

  const deleteAll = trpc.panel.deleteAll.useMutation({
    onSuccess: () => utils.panel.list.invalidate({ workspaceId })
  })

  const exitAllFullscreen = trpc.panel.exitAllFullscreen.useMutation({
    onSuccess: () => utils.panel.list.invalidate({ workspaceId })
  })

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't process hotkeys if user is typing in an input field
      if (!shouldProcessHotkey(e)) {
        return
      }
      
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsCommandPaletteOpen(true)
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault()
        handleQuickCreate('KANBAN')
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '?') {
        e.preventDefault()
        setIsKeyboardShortcutsOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Layout handlers
  const handleSavePreset = async () => {
    if (!saveForm.name.trim()) return

    const workspaceData = {
      name: saveForm.name.trim(),
      description: saveForm.description.trim() || undefined,
      isDefault: saveForm.isDefault,
    }

    try {
      const result = await savePreset(saveForm.name.trim(), saveForm.description.trim() || undefined, saveForm.isDefault)
      
      // Only close dialog and clear form on success
      setSaveForm({ name: '', description: '', isDefault: false })
      setSaveDialogOpen(false)
      
      // Force refresh the presets list
      await refetchPresets()
    } catch (error) {
      console.error('❌ Failed to save layout preset:', error)
    }
  }

  const handleLoadPreset = (presetId: string) => {
    loadPreset(presetId)
  }

  const handleDeletePreset = (presetId: string) => {
    deletePreset(presetId)
  }

  const handleSetDefault = (presetId: string) => {
    setDefaultPreset(presetId)
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Panel creation handlers
  const handleQuickCreate = (type: string) => {
    const panelType = PANEL_TYPES.find(p => p.type === type)
    if (!panelType) return

    let data: Record<string, any> = { workspaceId }
    let size = { width: 4, height: 3 }

    if (type === 'KANBAN') {
      size = { width: 8, height: 6 }
      // Create new empty Kanban board - don't reference existing ones
      data = { workspaceId, createNew: true }
    } else if (type === 'CHAT') {
      size = { width: 4, height: 5 }
      // Create new chat thread - don't reference existing ones
      data = { workspaceId, createNew: true }
    } else if (type === 'NOTES') {
      size = { width: 6, height: 5 }
      // Notes panel starts empty by default
      data = { workspaceId, createNew: true }
    }

    createPanel.mutate({
      type: type as any,
      title: panelType.defaultTitle,
      workspaceId,
      position: { x: 0, y: 0 },
      size,
      data
    })

    // Don't call onCreatePanel here as it would create a duplicate panel
  }

  const handleCustomCreate = () => {
    if (!selectedType) return

    const panelType = PANEL_TYPES.find(p => p.type === selectedType)
    if (!panelType) return

    const title = customTitle.trim() || panelType.defaultTitle

    let data: Record<string, any> = { workspaceId, createNew: true }
    let size = { width: 4, height: 3 }
    
    if (selectedType === 'KANBAN') {
      size = { width: 8, height: 6 }
    } else if (selectedType === 'CHAT') {
      size = { width: 4, height: 5 }
    } else if (selectedType === 'NOTES') {
      size = { width: 6, height: 5 }
    }

    createPanel.mutate({
      type: selectedType as any,
      title,
      workspaceId,
      position: { x: 0, y: 0 },
      size,
      data
    })
  }

  // Panel control handlers
  const handleMinimizeAll = () => {
    const hasMinimized = panels.some((panel: any) => panel.is_minimized)
    toggleMinimizeAll.mutate({ 
      workspaceId, 
      minimize: !hasMinimized
    })
  }

  const handleLockAll = () => {
    const hasLocked = panels.some((panel: any) => panel.is_locked)
    toggleLockAll.mutate({ 
      workspaceId, 
      lock: !hasLocked
    })
  }

  const handleDeleteAll = () => {
    if (confirm(`Delete all ${panels.length} panels in this workspace?`)) {
      deleteAll.mutate({ workspaceId })
    }
  }

  const handleExitAllFullscreen = () => {
    exitAllFullscreen.mutate({ workspaceId })
  }

  // Panel state checks
  const hasMinimizedPanels = panels.some((panel: any) => panel.is_minimized)
  const hasLockedPanels = panels.some((panel: any) => panel.is_locked)
  const hasFullscreenPanels = panels.some((panel: any) => {
    try {
      const panelData = typeof panel.data === 'string' ? JSON.parse(panel.data) : panel.data
      return panelData?.isFullscreen === true
    } catch {
      return false
    }
  })

  return (
    <TooltipProvider>
      <header 
        className={cn(
          // Make the header stay pinned at the top of the scrolling container
          "krushr-header-base krushr-header-workspace px-4 flex items-center justify-between w-full sticky top-0 z-40",
          className
        )}>
        {/* Mobile Layout (below md) */}
        <div className="md:hidden flex items-center w-full">
          {/* Left: Workspace */}
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="icon-xs" 
              className="krushr-header-button"
              aria-label="Workspace menu" title="Workspace menu"
              onClick={() => setIsMobileWorkspaceOpen(true)}
            >
              <FolderIcon className="w-3.5 h-3.5 text-krushr-primary" />
            </Button>
          </div>
          {/* Center: Title */}
          <div className="flex-1 min-w-0 text-center px-2">
            <span className="block text-sm font-medium text-gray-900 truncate">
              {currentWorkspace?.name || 'Workspace'}
            </span>
          </div>
          {/* Right: Search + Create */}
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon-xs" 
              onClick={() => setIsCommandPaletteOpen(true)}
              className="krushr-header-button" aria-label="Open search" title="Open search"
            >
              <SearchIcon className="w-3.5 h-3.5 text-gray-600" />
            </Button>
            <Button 
              onClick={() => setIsMobileCreateOpen(true)}
              size="icon-xs"
              className="bg-krushr-primary hover:bg-krushr-primary/90 text-white shadow-sm" aria-label="Create" title="Create"
            >
              <PlusIcon className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Mobile Workspace Sheet */}
        <Sheet open={isMobileWorkspaceOpen} onOpenChange={setIsMobileWorkspaceOpen}>
          <SheetContent side="top" className="h-[85vh] sm:h-[80vh] p-0 rounded-b-2xl sm:max-w-screen-sm sm:mx-auto sm:left-0 sm:right-0">
            {/* Header */}
            <div className="px-4 py-4 bg-gradient-to-r from-white via-gray-50 to-white border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FolderIcon className="w-4 h-4 text-krushr-primary" />
                  <span className="font-medium">Workspace</span>
                </div>
                <Badge variant={isOnline ? 'default' : 'destructive'} className="text-xs">
                  {isOnline ? 'Connected' : 'Offline'}
                </Badge>
              </div>
              {/* Search */}
              <div className="mt-3">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    placeholder="Search workspaces"
                    className="w-full pl-8 pr-2 h-10 border border-gray-200 rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-krushr-primary/30 focus:border-krushr-primary"
                    aria-label="Search workspaces"
                  />
                </div>
              </div>
            </div>

            {/* Workspace list */}
            <div className="p-3 space-y-2 overflow-y-auto" style={{ maxHeight: '50vh' }}>
              {workspaces.map((workspace: any) => (
                <button
                  key={workspace.id}
                  onClick={() => {
                    onNavigate?.(`/workspace/${workspace.id}`)
                    setIsMobileWorkspaceOpen(false)
                  }}
                  className="w-full text-left px-3 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-krushr-primary/30"
                >
                  <FolderIcon className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{workspace.name}</div>
                    <div className="text-xs text-gray-500">{workspace.memberCount || 0} members</div>
                  </div>
                  {workspace.id === workspaceId && (
                    <Badge variant="secondary" className="text-xs">Current</Badge>
                  )}
                </button>
              ))}
            </div>

            {/* Quick Actions grid */}
            <div className="p-4 border-t bg-gray-50 pb-[env(safe-area-inset-bottom)]">
              <div className="text-xs font-medium text-gray-600 mb-3">Quick actions</div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Kanban', icon: LayoutGrid, action: () => handleQuickCreate('KANBAN') },
                  { label: 'Notes', icon: StickyNote, action: () => handleQuickCreate('NOTES') },
                  { label: 'Chat', icon: MessageCircle, action: () => handleQuickCreate('CHAT') },
                  { label: 'Calendar', icon: Calendar, action: () => handleQuickCreate('CALENDAR') },
                  { label: 'Save', icon: Save, action: () => setSaveDialogOpen(true) },
                  { label: 'Focus', icon: CommandIcon, action: () => onAction?.('focus-mode') },
                  { label: 'Dashboard', icon: LayoutGrid, action: () => handleQuickCreate('DASHBOARD') },
                  { label: 'Settings', icon: Settings2, action: () => onNavigate?.('/settings') },
                ].map(({ label, icon: Icon, action }) => (
                  <button
                    key={label}
                    onClick={() => {
                      action()
                      setIsMobileWorkspaceOpen(false)
                    }}
                    className="px-3 py-3 rounded-xl bg-white border border-gray-200 hover:bg-gray-100 text-xs font-medium flex flex-col items-center gap-2 focus:outline-none focus:ring-2 focus:ring-krushr-primary/30"
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-krushr-primary/10 text-krushr-primary">
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="truncate">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Mobile Create Sheet */}
        <Sheet open={isMobileCreateOpen} onOpenChange={setIsMobileCreateOpen}>
          <SheetContent side="bottom" className="p-4 pb-[env(safe-area-inset-bottom)]">
            <SheetHeader>
              <SheetTitle>Create</SheetTitle>
              <SheetDescription>Select what you’d like to create.</SheetDescription>
            </SheetHeader>
            <div className="grid grid-cols-3 gap-3 mt-3">
              {[
                { label: 'Kanban', icon: LayoutGrid, action: () => handleQuickCreate('KANBAN') },
                { label: 'Notes', icon: StickyNote, action: () => handleQuickCreate('NOTES') },
                { label: 'Chat', icon: MessageCircle, action: () => handleQuickCreate('CHAT') },
                { label: 'Calendar', icon: Calendar, action: () => handleQuickCreate('CALENDAR') },
              ].map(({ label, icon: Icon, action }) => (
                <button
                  key={label}
                  onClick={() => {
                    action()
                    setIsMobileCreateOpen(false)
                  }}
                  className="px-3 py-3 rounded-xl bg-white border border-gray-200 hover:bg-gray-100 text-xs font-medium flex flex-col items-center gap-2 focus:outline-none focus:ring-2 focus:ring-krushr-primary/30"
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-krushr-primary/10 text-krushr-primary">
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </SheetContent>
        </Sheet>

        {/* Desktop Layout - Hidden on mobile, shown on md and up */}
        <div className="hidden md:contents">
          {/* Desktop: Left Zone - Context & Identity */}
          <div className="flex items-center space-x-1 lg:space-x-2">
          {/* Workspace Selector Dropdown - Desktop */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-10 px-2 lg:px-3 hover:bg-white/20 relative">
                <div className="flex items-center space-x-1 lg:space-x-2">
                  <FolderIcon className="w-4 h-4 text-krushr-primary" />
                  <span className="font-semibold text-gray-900 text-sm max-w-20 lg:max-w-32 truncate">
                    {currentWorkspace?.name || 'Workspace'}
                  </span>
                  <ChevronDownIcon className="w-3 h-3 text-gray-500 ml-1" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="start" 
              sideOffset={5}
              collisionPadding={20}
              className="w-72 max-w-[calc(100vw-2rem)]">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Workspaces</span>
                <Badge variant={isOnline ? 'default' : 'destructive'} className="text-xs">
                  {isOnline ? 'Connected' : 'Offline'}
                </Badge>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {/* Search Bar */}
              <div className="px-2 pb-2">
                <div className="relative">
                  <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    placeholder="Search workspaces..."
                    className="w-full pl-7 pr-2 h-8 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-krushr-primary/30 focus:border-krushr-primary"
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Workspace List */}
              <div className="max-h-64 overflow-y-auto">
                {workspaces
                  .filter((workspace: any) => 
                    searchQuery ? workspace.name.toLowerCase().includes(searchQuery.toLowerCase()) : true
                  )
                  .map((workspace: any) => (
                    <DropdownMenuItem
                      key={workspace.id}
                      onClick={() => onNavigate?.(`/workspace/${workspace.id}`)}
                      className="flex items-start gap-3 p-3"
                    >
                      <FolderIcon className="w-4 h-4 mt-0.5 text-krushr-secondary" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{workspace.name}</div>
                        <div className="text-xs text-gray-500">{workspace.memberCount || 0} members</div>
                      </div>
                      {workspace.id === workspaceId && (
                        <Badge variant="secondary" className="text-xs">Current</Badge>
                      )}
                    </DropdownMenuItem>
                  ))
                }
              </div>
              
              <DropdownMenuSeparator />
              
              {/* Quick Actions */}
              <DropdownMenuLabel className="text-xs text-gray-500 uppercase tracking-wide">Quick Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleQuickCreate('KANBAN')} className="flex items-start gap-3 p-3">
                <Columns className="w-4 h-4 mt-0.5 text-krushr-secondary" />
                <div>
                  <div className="font-medium text-sm">New Kanban Board</div>
                  <div className="text-xs text-gray-500">Task management with columns</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleQuickCreate('NOTES')} className="flex items-start gap-3 p-3">
                <StickyNote className="w-4 h-4 mt-0.5 text-krushr-secondary" />
                <div>
                  <div className="font-medium text-sm">New Notes Panel</div>
                  <div className="text-xs text-gray-500">Quick notes and documentation</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleQuickCreate('CHAT')} className="flex items-start gap-3 p-3">
                <MessageCircle className="w-4 h-4 mt-0.5 text-krushr-secondary" />
                <div>
                  <div className="font-medium text-sm">New Chat Panel</div>
                  <div className="text-xs text-gray-500">Team communication</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleQuickCreate('CALENDAR')} className="flex items-start gap-3 p-3">
                <Calendar className="w-4 h-4 mt-0.5 text-krushr-secondary" />
                <div>
                  <div className="font-medium text-sm">New Calendar</div>
                  <div className="text-xs text-gray-500">Schedule and events</div>
                </div>
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              {/* Workspace Management */}
              <DropdownMenuItem onClick={() => handleQuickCreate('DASHBOARD')} className="flex items-start gap-3 p-3">
                <LayoutGrid className="w-4 h-4 mt-0.5 text-krushr-primary" />
                <div className="flex-1">
                  <div className="font-medium text-sm">Dashboard</div>
                  <div className="text-xs text-gray-500">Workspace overview</div>
                </div>
                <DropdownMenuShortcut>⌘D</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction?.('focus-mode')} className="flex items-start gap-3 p-3">
                <CommandIcon className="w-4 h-4 mt-0.5 text-krushr-primary" />
                <div className="flex-1">
                  <div className="font-medium text-sm">Focus Mode</div>
                  <div className="text-xs text-gray-500">Hide distractions</div>
                </div>
                <DropdownMenuShortcut>⌘⇧F</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onNavigate?.('/settings')} className="flex items-start gap-3 p-3">
                <Settings2 className="w-4 h-4 mt-0.5 text-krushr-primary" />
                <div>
                  <div className="font-medium text-sm">Workspace Settings</div>
                  <div className="text-xs text-gray-500">Configure workspace</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* End workspace selector dropdown */}

          {/* Layout Quick Actions */}
          <div className="hidden lg:flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSaveDialogOpen(true)}
              disabled={panels.length === 0}
              className="h-8 w-8 p-0 hover:bg-green-500/30 hover:text-white transition-colors"
              title="Save Layout"
            >
              <Save className="w-4 h-4" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={presets.length === 0}
                  className="h-8 w-8 p-0 hover:bg-blue-500/30 hover:text-white transition-colors"
                  title="Load Layout"
                >
                  <FolderOpen className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-80">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Load Layout Preset</span>
                  <Badge variant="outline" className="text-xs">{presets.length} saved</Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {presets.length === 0 ? (
                  <div className="text-center py-6 px-4 text-gray-500">
                    <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm font-medium">No saved layouts</p>
                    <p className="text-xs mt-1">Save your current layout to get started</p>
                  </div>
                ) : (
                  <>
                    <div className="max-h-80 overflow-y-auto">
                      {presets.map((preset) => (
                        <div key={preset.id} className="group">
                          <DropdownMenuItem
                            onClick={() => handleLoadPreset(preset.id)}
                            disabled={isLoading}
                            className="flex items-start gap-3 p-3"
                          >
                            <FolderOpen className="w-4 h-4 mt-0.5 text-krushr-secondary" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{preset.name}</div>
                              <div className="text-xs text-gray-500">
                                {preset.description || 'No description'}
                                {preset.isDefault && ' • Default'}
                              </div>
                            </div>
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                              {!preset.isDefault && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleSetDefault(preset.id)
                                  }}
                                  title="Set as default"
                                >
                                  <Star className="w-3 h-3 text-orange-600" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (confirm(`Delete layout "${preset.name}"?`)) {
                                    handleDeletePreset(preset.id)
                                  }
                                }}
                                title="Delete"
                              >
                                <Trash2 className="w-3 h-3 text-red-600" />
                              </Button>
                            </div>
                          </DropdownMenuItem>
                        </div>
                      ))}
                    </div>
                    
                    {presets.length > 3 && (
                      <>
                        <DropdownMenuSeparator />
                        <div className="px-2 py-1.5 text-xs text-gray-500">
                          Scroll for more presets
                        </div>
                      </>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Center Zone - Unified Action Hub */}
        <div className="flex-1 flex items-center justify-center space-x-1 lg:space-x-2 max-w-4xl mx-2 lg:mx-8 min-w-0">
          
          {/* Smart Search Command Center */}
          <div className="relative flex-1 max-w-md min-w-0">
            <SearchIcon className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-500 z-10" />
            <Input
              placeholder="Search, create, or command (⌘K)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-14 lg:pr-24 h-9 krushr-header-search focus:border-krushr-primary/50 min-w-0"
              onFocus={() => setIsCommandPaletteOpen(true)}
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              <Button
                size="sm"
                onClick={() => setIsCommandPaletteOpen(true)}
                className="h-6 px-1 lg:px-2 bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200 text-xs hidden sm:inline-flex"
              >
                ⌘K
              </Button>
            </div>
          </div>

          {/* Compact Creation Toolbar - Medium screens */}
          <div className="hidden md:flex lg:hidden items-center space-x-1">
            {/* Top 2 most used on medium screens */}
            {PANEL_TYPES.filter(p => ['KANBAN', 'CHAT'].includes(p.type)).map(({ type, icon: Icon, label }) => (
              <Button
                key={type}
                variant="ghost"
                size="sm"
                onClick={() => handleQuickCreate(type)}
                disabled={createPanel.isPending}
                className="h-8 w-8 p-0 hover:bg-white hover:shadow-sm transition-all"
                title={`Create ${label}`}
              >
                <Icon className="w-4 h-4" />
              </Button>
            ))}
            
            {/* More Options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-white/20 hover:shadow-sm">
                  <MoreHorizontalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-64">
                <DropdownMenuLabel>Create Panel</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {PANEL_TYPES.map(({ type, icon: Icon, label, description }) => (
                  <DropdownMenuItem
                    key={type}
                    onClick={() => handleQuickCreate(type)}
                    className="flex items-start gap-3 p-3"
                  >
                    <Icon className="w-4 h-4 mt-0.5 text-krushr-secondary" />
                    <div>
                      <div className="font-medium text-sm">{label}</div>
                      <div className="text-xs text-gray-500">{description}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Full Creation Toolbar - Large screens */}
          <div className="hidden lg:flex items-center space-x-1 krushr-header-button-group p-1">
            {/* Quick Create - Top 4 Most Used Panel Types */}
            {PANEL_TYPES.filter(p => ['KANBAN', 'CHAT', 'NOTES', 'CALENDAR'].includes(p.type)).map(({ type, icon: Icon, label }) => (
              <Button
                key={type}
                variant="ghost"
                size="sm"
                onClick={() => handleQuickCreate(type)}
                disabled={createPanel.isPending}
                className={cn(
                  "h-8 w-8 p-0 hover:bg-white hover:shadow-sm transition-all",
                  type === 'KANBAN' && "hover:bg-krushr-secondary hover:text-white",
                  type === 'CHAT' && "hover:bg-krushr-success hover:text-white",
                  type === 'NOTES' && "hover:bg-krushr-warning hover:text-white", 
                  type === 'CALENDAR' && "hover:bg-krushr-info hover:text-white"
                )}
                title={`Create ${label}`}
              >
                <Icon className="w-4 h-4" />
              </Button>
            ))}
            
            {/* More Creation Options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-white/20 hover:shadow-sm">
                  <MoreHorizontalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-64">
                <DropdownMenuLabel>More Panel Types</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {PANEL_TYPES.filter(p => !['KANBAN', 'CHAT', 'NOTES', 'CALENDAR'].includes(p.type)).map(({ type, icon: Icon, label, description }) => (
                  <DropdownMenuItem
                    key={type}
                    onClick={() => handleQuickCreate(type)}
                    className="flex items-start gap-3 p-3"
                  >
                    <Icon className="w-4 h-4 mt-0.5 text-krushr-secondary" />
                    <div>
                      <div className="font-medium text-sm">{label}</div>
                      <div className="text-xs text-gray-500">{description}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsCustomPanelOpen(true)} className="p-3">
                  <Settings2 className="w-4 h-4 mr-3 text-krushr-primary" />
                  <div>
                    <div className="font-medium text-sm">Custom Panel</div>
                    <div className="text-xs text-gray-500">Configure your own panel type</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* AI Assistant - Always Accessible */}
          <div className="flex items-center flex-shrink-0">
            <Button
              size="sm"
              onClick={() => onAction?.('ai-assistant')}
              className="relative h-8 w-8 p-0 bg-gradient-to-r from-krushr-primary to-krushr-secondary hover:from-krushr-primary/90 hover:to-krushr-secondary/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group overflow-hidden border border-white/20"
              title="AI Assistant (⌘⇧A)"
            >
              {/* Animated background shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"></div>
              
              {/* Subtle inner glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-krushr-primary/50 to-krushr-secondary/50 blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Icon with pulse animation */}
              <ZapIcon className="relative w-4 h-4 drop-shadow-sm group-hover:drop-shadow-md transition-all duration-300 group-hover:scale-110" />
              
              {/* Subtle pulse ring on hover */}
              <div className="absolute inset-0 rounded border-2 border-white/30 opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300"></div>
            </Button>
          </div>
        </div>

          {/* Right Zone - Session & Control */}
          <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0 min-w-0">
          
          {/* Workspace Management Hub */}
          {panels.length > 0 && (
            <div className="flex items-center krushr-header-button-group p-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 px-2 lg:px-3 hover:bg-white hover:shadow-sm transition-all flex items-center gap-1 lg:gap-2"
                  >
                    <Settings2 className="w-4 h-4" />
                    <span className="text-sm font-medium hidden lg:inline">Manage</span>
                    <Badge variant="secondary" className="text-xs px-1 lg:px-1.5 bg-krushr-primary/10 text-krushr-primary">
                      {panels.length}
                    </Badge>
                  </Button>
                </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Panel Management</span>
                  <Badge variant="outline" className="text-xs">{panels.length} active</Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {/* Active Panels List */}
                {panels.length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-xs text-gray-500 uppercase tracking-wide">Active Panels</DropdownMenuLabel>
                    <div className="max-h-40 overflow-y-auto">
                      {panels.map((panel: any) => {
                        const panelType = PANEL_TYPES.find(p => p.type === panel.type)
                        const Icon = panelType?.icon || FolderIcon
                        return (
                          <DropdownMenuItem
                            key={panel.id}
                            className="flex items-center gap-2 px-3 py-2 text-sm cursor-default"
                          >
                            <Icon className="w-4 h-4 text-krushr-secondary flex-shrink-0" />
                            <span className="flex-1 truncate">{panel.title || panelType?.label}</span>
                            <div className="flex items-center gap-1">
                              {panel.is_minimized && (
                                <Minimize2 className="w-3 h-3 text-gray-400" title="Minimized" />
                              )}
                              {panel.is_locked && (
                                <Lock className="w-3 h-3 text-gray-400" title="Locked" />
                              )}
                              {(() => {
                                try {
                                  const panelData = typeof panel.data === 'string' ? JSON.parse(panel.data) : panel.data
                                  return panelData?.isFullscreen && (
                                    <Maximize2 className="w-3 h-3 text-gray-400" title="Fullscreen" />
                                  )
                                } catch {
                                  return null
                                }
                              })()}
                            </div>
                          </DropdownMenuItem>
                        )
                      })}
                    </div>
                    <DropdownMenuSeparator />
                  </>
                )}
                
                {/* Bulk Actions */}
                <DropdownMenuLabel className="text-xs text-gray-500 uppercase tracking-wide">Bulk Actions</DropdownMenuLabel>
                <DropdownMenuItem 
                  onClick={handleMinimizeAll}
                  disabled={toggleMinimizeAll.isPending}
                  className="flex items-center gap-2"
                >
                  {hasMinimizedPanels ? (
                    <>
                      <Maximize2 className="w-4 h-4" />
                      Restore All Panels
                    </>
                  ) : (
                    <>
                      <Minimize2 className="w-4 h-4" />
                      Minimize All Panels
                    </>
                  )}
                </DropdownMenuItem>
                
                <DropdownMenuItem 
                  onClick={handleLockAll}
                  disabled={toggleLockAll.isPending}
                  className="flex items-center gap-2"
                >
                  {hasLockedPanels ? (
                    <>
                      <Unlock className="w-4 h-4" />
                      Unlock All Panels
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Lock All Panels
                    </>
                  )}
                </DropdownMenuItem>

                {hasFullscreenPanels && (
                  <DropdownMenuItem 
                    onClick={handleExitAllFullscreen}
                    disabled={exitAllFullscreen.isPending}
                    className="flex items-center gap-2"
                  >
                    <Minimize2 className="w-4 h-4" />
                    Exit All Fullscreen
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />
                
                {/* Focus Mode */}
                <DropdownMenuLabel className="text-xs text-gray-500 uppercase tracking-wide">Workspace Mode</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onAction?.('focus-mode')}>
                  <span>Focus Mode</span>
                  <DropdownMenuShortcut>⌘⇧F</DropdownMenuShortcut>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {/* Danger Zone */}
                <DropdownMenuLabel className="text-xs text-red-500 uppercase tracking-wide">Danger Zone</DropdownMenuLabel>
                <DropdownMenuItem 
                  onClick={handleDeleteAll}
                  disabled={deleteAll.isPending}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete All Panels
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          )}

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0 hover:bg-white/20 relative"
                title="Notifications"
              >
                <BellIcon className="h-4 w-4" />
                {notifications.filter(n => !n.isRead).length > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-xs bg-red-500">
                    {notifications.filter(n => !n.isRead).length > 9 ? '9+' : notifications.filter(n => !n.isRead).length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notifications</span>
                {notifications.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => {
                      // Mark all as read
                      notifications.forEach(n => {
                        if (!n.isRead) {
                          // Call mutation to mark as read
                        }
                      })
                    }}
                  >
                    Mark all read
                  </Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {notifications.length > 0 ? (
                <ScrollArea className="h-[300px]">
                  {notifications.map((notification, idx) => (
                    <DropdownMenuItem
                      key={notification.id || idx}
                      className="p-3 cursor-pointer"
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className={cn(
                          "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                          notification.isRead ? "bg-gray-300" : "bg-blue-500"
                        )} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notification.createdAt).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </ScrollArea>
              ) : (
                <div className="p-8 text-center text-sm text-gray-500">
                  <BellIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  No new notifications
                </div>
              )}
              
              {notifications.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="justify-center text-sm font-medium">
                    View all notifications
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>


          {/* User Profile & Session */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 px-1 lg:px-2 rounded-full hover:bg-white/20">
                <Avatar className="h-7 w-7 lg:mr-2">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="text-xs bg-krushr-primary text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden lg:flex flex-col items-start mr-2">
                  <span className="text-sm font-medium truncate max-w-20">
                    {user?.name || 'User'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
                <ChevronDownIcon className="w-3 h-3 text-gray-500 hidden lg:inline" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="text-sm bg-krushr-primary text-white">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <p className="text-sm font-medium">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        isOnline ? "bg-green-500" : "bg-red-500"
                      )} />
                      <span className="text-xs text-gray-500">
                        {isOnline ? 'Connected' : 'Offline'}
                      </span>
                    </div>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {/* Profile Actions */}
              <DropdownMenuLabel className="text-xs text-gray-500 uppercase tracking-wide">Account</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="flex items-start gap-3 p-3">
                <SettingsIcon className="w-4 h-4 mt-0.5 text-krushr-primary" />
                <div>
                  <div className="font-medium text-sm">Settings</div>
                  <div className="text-xs text-gray-500">Account preferences</div>
                </div>
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              {/* Help & Support */}
              <DropdownMenuLabel className="text-xs text-gray-500 uppercase tracking-wide">Help & Support</DropdownMenuLabel>
              
              <DropdownMenuItem onClick={() => setIsKeyboardShortcutsOpen(true)}>
                <KeyboardIcon className="w-4 h-4 mr-2" />
                Keyboard Shortcuts
                <DropdownMenuShortcut>⌘?</DropdownMenuShortcut>
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => onNavigate?.('/docs')}>
                <FileText className="w-4 h-4 mr-2" />
                Documentation
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => window.open('https://github.com/krushr-project/krushr/discussions', '_blank')}>
                <MessageSquare className="w-4 h-4 mr-2" />
                Community Forum
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => window.open('mailto:feedback@krushr.dev?subject=Krushr Feedback&body=Hi Krushr team,%0A%0AI have feedback about:%0A%0A[Please describe your feedback here]%0A%0AThanks!', '_blank')}>
                <MessageCircle className="w-4 h-4 mr-2" />
                Send Feedback
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => onAction?.('about')}>
                <Info className="w-4 h-4 mr-2" />
                About Krushr
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              {/* Session Control */}
              <DropdownMenuLabel className="text-xs text-gray-500 uppercase tracking-wide">Session</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onAction?.('logout')}>
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        </div>
        {/* End Desktop Layout */}

        {/* Save Layout Dialog */}
        <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <DialogContent 
            className="sm:max-w-md"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && saveForm.name.trim() && !isSaving && panels.length > 0) {
                e.preventDefault()
                handleSavePreset()
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>Save Layout Preset</DialogTitle>
              <DialogDescription>
                Save your current panel arrangement as a reusable layout preset.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="preset-name">Preset Name</Label>
                <FloatingInput
                  id="preset-name"
                  label="Preset Name"
                  value={saveForm.name}
                  onChange={(e) => setSaveForm(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="preset-description">Description (Optional)</Label>
                <Textarea
                  id="preset-description"
                  value={saveForm.description}
                  onChange={(e) => setSaveForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this layout arrangement..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is-default"
                  checked={saveForm.isDefault}
                  onChange={(e) => setSaveForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is-default" className="text-sm">
                  Set as default layout for this workspace
                </Label>
              </div>
            </div>

            {/* Live Preview */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
              <div className="text-xs font-medium text-gray-600 mb-2">Preview:</div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{saveForm.name || 'Untitled Layout'}</div>
                  <div className="text-xs text-gray-500">
                    {saveForm.description || 'No description'}
                    {saveForm.isDefault && ' • Default Layout'}
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {panels.length} panels
                </Badge>
              </div>
            </div>

            {/* Action Buttons in Proper Footer */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200 mt-6">
              <div className="text-xs text-gray-500">
                {panels.length === 0 ? 'No panels to save' : `Saving ${panels.length} panel${panels.length === 1 ? '' : 's'}`}
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setSaveDialogOpen(false)}
                  className="px-4"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSavePreset}
                  disabled={!saveForm.name.trim() || isSaving || panels.length === 0}
                  className="bg-gradient-to-r from-krushr-primary to-krushr-secondary hover:from-krushr-primary/90 hover:to-krushr-secondary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 px-6"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Layout
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        {/* Custom Panel Creation Dialog */}
        <Dialog open={isCustomPanelOpen} onOpenChange={setIsCustomPanelOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle>Create Custom Panel</DialogTitle>
              <DialogDescription>
                Choose a panel type and customize its settings.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Panel type selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Panel Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PANEL_TYPES.map(({ type, icon: Icon, label, description }) => (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      className={cn(
                        "p-3 border rounded-lg text-left transition-all",
                        selectedType === type
                          ? "border-krushr-primary bg-krushr-primary/5"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-krushr-secondary" />
                        <span className="font-medium text-sm">{label}</span>
                      </div>
                      <p className="text-xs text-gray-600">{description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom title */}
              {selectedType && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Panel Title
                  </label>
                  <FloatingInput
                    label="Panel Title"
                    placeholder={PANEL_TYPES.find(p => p.type === selectedType)?.defaultTitle}
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                  />
                </div>
              )}

              {/* Create button */}
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setIsCustomPanelOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCustomCreate}
                  disabled={!selectedType || createPanel.isPending}
                  className="bg-krushr-primary hover:bg-krushr-primary/90"
                >
                  {createPanel.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <PlusIcon className="w-4 h-4 mr-2" />
                  )}
                  Create Panel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Command Palette */}
        <CommandPalette
          isOpen={isCommandPaletteOpen}
          onOpenChange={setIsCommandPaletteOpen}
          workspaceId={workspaceId}
          onNavigate={onNavigate}
          onCreatePanel={onCreatePanel}
          onAction={onAction}
        />

        {/* Keyboard Shortcuts Dialog */}
        <KeyboardShortcuts
          isOpen={isKeyboardShortcutsOpen}
          onOpenChange={setIsKeyboardShortcutsOpen}
        />
      </header>
    </TooltipProvider>
  )
}